import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewEncapsulation,
  computed,
  effect,
  input,
  signal,
  inject,
  DestroyRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControlStatus, FormGroup } from '@angular/forms';
import { FIELD_ERROR_ANIMATION, NumericSliderComponent, SelectOnFocusDirective, TResponsiveSize } from '@partners/ui';
import { MaskingImportPack, coerceNumber, isNullOrUndefined, provideMasking } from '@partners/utils';
import { tap } from 'rxjs';
import { FormDefaultImportPack } from '../../import-pack';
import { BaseCustomControl } from '../custom-control';

/**
 * a custom composite control with (1) mat-form-field numeric input and (2) numeric slider
 * the form control is binded only to mar-form-field, the slider is a helper control providing
 * another way to feed the form control its value.
 *
 * changes to the slider updates the input field and vice versa
 *
 * inherits base custom control which provides basic value accessor requirements
 *
 * fieldAccessor (member in parent) is being used to access the form field config and events
 * (see valueChange event below)
 */

@Component({
  selector: 'dual-numeric-input',
  standalone: true,
  imports: [FormDefaultImportPack, NumericSliderComponent, SelectOnFocusDirective, MaskingImportPack],
  providers: [provideMasking()],
  template: `
  
  <ng-container [formGroup]="formGroup()">

      <mat-form-field class="xxl-input" [class]="inputWidth()">
        @defer() {
        <mat-label><span class="font--md font--light">{{ fieldAccessor().label }}</span></mat-label>
        }
        <input matInput [formControlName]="controlName()" 
        
        #input
        [validation]="false"
        [disabled]="fieldAccessor().control.disabled"
        [inputMode]="fieldAccessor().inputMode"
        [type]="fieldAccessor().inputType" selectOnFocus
        [showMaskTyped]="masking().showMaskPattern"
        [thousandSeparator]="masking().delimiterChar"
        [prefix]="masking().prefix"
        [dropSpecialCharacters]="masking().clearForModel"        
            [mask]="masking().maskPattern" />
      </mat-form-field>


      <div style="min-height:2rem">

      <numeric-slider
        [disabled]="this.fieldAccessor().control.disabled"
        [hideTickLabels]="showError()"
        (valueChange)="onSliderChange($event)"
        [maxValue]="maxValue()"
        [minValue]="minValue()" [step]="step()" [value]="inputValue()" />

        @if(showError()){

          <mat-error [@fieldError]>
            <field-error [fieldConfig]="fieldAccessor()"></field-error>
          </mat-error>
        }
        </div>
  </ng-container>
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [FIELD_ERROR_ANIMATION]
})
export class DualNumericInputComponent extends BaseCustomControl<number> implements OnInit {
  /** min value - overflow shows min invalid error */
  minValue = input<number>(0);

  /** max value - overflow shows max invalid error */
  maxValue = input<number>(100);

  /** data intervals for the slider */
  step = input<number>(1);

  inputSize = input<TResponsiveSize>('md');

  /** ui requirements demands different length of input field */
  inputWidth = computed(() => `width--${this.inputSize()}`);

  /** we must bind the parent form group of the custom control to please angular's demands */
  formGroup = input<FormGroup>();

  /** signal that changes each time the user types amount in textbox */
  inputValue = signal<number>(0);
  showError = signal<boolean>(false);

  masking = computed(() => this.fieldAccessor().masking);

  #effectRef = effect(() => {
    const fieldAccessor = this.fieldAccessor();
    if (isNullOrUndefined(fieldAccessor)) {
      return;
    }

    fieldAccessor.addValueChangeListener(({ nextValue }, { field }) => {
      this.inputValue.set(coerceNumber(nextValue));

      // /** toggle subscript display between error & slider labels, when on is shown - the other is hidden */
      // const { invalid, dirty, touched } = field.control;
      // const isErrored = invalid && (dirty || touched);
      // this.showError.set(isErrored);
    });

    this.#effectRef.destroy();
  });

  /**
   * captures the value change event of the slider. we update the binded form control via control
   * value accessor's "writeValue" method implemented in the extended component
   **/
  onSliderChange(value: number) {
    this.writeValue(value);
  }

  #destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    /**
     * listen to changes made in the input textbox, inform the local signal of the updated value
     * which will update the slider
     */
    // this.fieldAccessor().addValueChangeListener(({ nextValue, prevValue }, { field, config, form }) => {
    //   this.inputValue.set(coerceNumber(nextValue));
    // });

    this.inputValue.set(this.fieldAccessor().control.value as number);

    this.fieldAccessor().control.statusChanges.pipe(
      takeUntilDestroyed(this.#destroyRef),
      tap((status: FormControlStatus) => {
        this.showError.set(status === 'INVALID');
      })).subscribe();
  }
}
